import { useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Icons } from '@/components/ui/Icons'
import { TrendIcon } from '@/features/ratescreener/TrendIcon'
import type { AttentionEntry, AttentionItem, EntryTarget } from './attention'
import styles from './AttentionDialog.module.css'
import { SEVERITY } from './overview'

const COLLAPSED_ENTRIES = 6

/** One attention item in full: its explanation and every entry, each with a click-through. */
export function ItemCard({ item, onOpen }: { item: AttentionItem; onOpen: (target: EntryTarget) => void }) {
  const [expanded, setExpanded] = useState(false)
  const severity = SEVERITY[item.severity]
  const visible = expanded ? item.entries : item.entries.slice(0, COLLAPSED_ENTRIES)
  const hidden = item.entries.length - visible.length

  return (
    <article className={`${styles.item} ${styles[item.severity]}`}>
      <header className={styles.itemHead}>
        <Badge tone={severity.tone} icon={severity.icon}>
          {severity.label}
        </Badge>
        <h4 className={styles.title}>{item.title}</h4>
      </header>
      {item.detail && <p className={styles.detail}>{item.detail}</p>}
      {item.entries.length > 0 && (
        <>
          <ul className={styles.entries}>
            {visible.map((entry) => (
              <Entry key={entry.key} entry={entry} onOpen={onOpen} />
            ))}
          </ul>
          {(hidden > 0 || expanded) && item.entries.length > COLLAPSED_ENTRIES && (
            <Button size="sm" variant="ghost" onClick={() => setExpanded((e) => !e)}>
              {expanded ? 'Show fewer' : `Show ${hidden} more`}
            </Button>
          )}
        </>
      )}
    </article>
  )
}

function Entry({ entry, onOpen }: { entry: AttentionEntry; onOpen: (target: EntryTarget) => void }) {
  const { target } = entry
  return (
    <li>
      <span className={styles.nameCell}>
        {target ? (
          <button type="button" className={`u-linkButton ${styles.name}`} onClick={() => onOpen(target)} title={target.kind === 'policy' ? 'Open the policy' : 'Open the details'}>
            {entry.label}
          </button>
        ) : (
          <span className={styles.name}>{entry.label}</span>
        )}
        {entry.priority && (
          <span className={styles.star} title="Priority POS">
            <Icons.Star size={14} aria-label="Priority POS" />
          </span>
        )}
      </span>
      <span className={styles.value}>
        {entry.value}
        {entry.trend && <TrendIcon trend={entry.trend.direction} reference={entry.trend.reference} />}
      </span>
      {entry.flags ? (
        <span className={styles.flags}>
          {entry.flags.map((flag) => (
            <Badge key={flag.label} tone={flag.enabled ? 'ok' : 'danger'} icon={flag.enabled ? 'circle-check' : 'ban'} title={`${flag.title} is ${flag.enabled ? 'on' : 'off'}`}>
              {flag.label}
            </Badge>
          ))}
        </span>
      ) : (
        <span className={styles.note}>{entry.note ?? ''}</span>
      )}
    </li>
  )
}
